import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { FavoriteButton, ProductReviewModal, Rating, Reviews, WaitButton } from 'components';
import config from 'config';
import { useOnMount } from 'hooks';
import { ArrowLeft as ArrowLeftIcon } from 'components/icons';
import {
  LoadingIndicator,
  Button,
  UpDownButton,
  AccordionDetails,
  Accordion,
  AccordionSummary,
  LazyImage,
} from 'components/shared';
import { useBrowserHistoryContext } from 'contexts/BrowserHistory';
import { useCartContext } from 'contexts/Cart';
import { useCustomerContext } from 'contexts/Customer';
import { useDarkstoreContext } from 'contexts/Darkstore';
import { useProduct } from 'hooks/data';
import { joinPaths } from 'utils/helpers';
import { closeModal, openModal } from 'utils/modals';
import { ReviewsTotal } from './ReviewsTotal';
import styles from './Product.module.scss';

const Product = ({
  match: {
    params: { productSlug },
  },
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { goBackOrReplace } = useBrowserHistoryContext();
  const { customer } = useCustomerContext();
  const { products: cartProducts, addProduct, setProductQty } = useCartContext();
  const { darkstore } = useDarkstoreContext();

  const { isLoading, product } = useProduct(productSlug);

  useOnMount(() => {
    document.body.style.backgroundColor = '#fff';
    return () => {
      document.body.style.backgroundColor = '#f9fafc';
    };
  });

  if (isLoading) {
    return <LoadingIndicator />;
  }

  const {
    barcode,
    img,
    name,
    weight,
    rating,
    reviews,
    oldPrice,
    currentPrice,
    currency,
    currencyISO,
    discount,
    qty,
    isWait,
    isFavorite,
    description,

    calorie,
    protein,
    fat,
    carbohydrate,

    manufacturer,
    importer,
    composition,
    expiryFull,
    regulatoryDocument,
    pricePerKg,

    isReviewed,
    brand,
    categoryId,
    isWeightGoods,
  } = product;

  const inCart = cartProducts.find(cartProduct => cartProduct.id === Number(product.id));

  const currentPriceWeightGood =
    currentPrice !== null && currentPrice !== undefined && currentPrice !== ''
      ? Number(currentPrice)
      : Number(pricePerKg || 0);
  const oldPriceWeightGood =
    oldPrice !== null && oldPrice !== undefined && oldPrice !== ''
      ? Number(oldPrice)
      : Number(pricePerKg || 0);
  const fixedDiscountValue =
    oldPriceWeightGood > currentPriceWeightGood
      ? (oldPriceWeightGood - currentPriceWeightGood).toFixed(2)
      : null;
  return (
    <>
      <Helmet>
        <title>
          {t('meta:product.title', {
            siteName: config.SITE_NAME,
            cityName: darkstore.city,
            productName: product.name,
          })}
        </title>
        <meta
          name="title"
          content={t('meta:product.title', {
            siteName: config.SITE_NAME,
            cityName: darkstore.city,
            productName: product.name,
          })}
        />
        <meta
          name="description"
          content={t('meta:product.description', {
            siteName: config.SITE_NAME,
            cityName: darkstore.city,
            productName: product.name,
          })}
        />
        <meta property="og:type" content="og:product" />
        <meta property="og:title" content={name} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={`${window.location.origin}/products/${productSlug}`} />
        <meta property="og:image" content={img} />
        <meta property="product:category" content={categoryId} />
        <meta property="product:brand" content={brand} />
        <meta property="product:availability" content="in stock" />
        <meta property="product:condition" content="new" />
        <meta property="product:price:amount" content={oldPrice || currentPrice} />
        <meta property="product:price:currency" content={currencyISO} />
        <meta property="product:retailer_item_id" content={barcode} />
      </Helmet>

      <div className={styles.container}>
        <div className={styles.header}>
          <div
            className={styles['header__back-button']}
            role="button"
            onClick={() => goBackOrReplace(joinPaths('/', language))}
          >
            <ArrowLeftIcon color="text" size={16} />
            {t('product:backToCatalog')}
          </div>
          <span className={styles['header__code']}>
            {barcode && (
              <>
                <b>{t('product:barcode')}:</b> {barcode}
              </>
            )}
          </span>
        </div>
        <div className={styles.content}>
          <div className={clsx(styles.image, styles['image--desktop'])}>
            <LazyImage src={img} alt={name} />
            {product.advIcon && (
              <img className={styles.advIcon} src={product.advIcon} alt="Adv icon" />
            )}
          </div>
          <div className={styles.info}>
            <div className={styles.title}>
              <h1>{name}</h1>
              {weight && <span>{weight}</span>}
            </div>
            <div className={styles.rating}>
              <Rating value={rating} />
              {t('product:reviews')}: {reviews.length}
            </div>
            <div className={clsx(styles.image, styles['image--mobile'])}>
              <LazyImage src={img} alt={name} />
              {product.advIcon && (
                <img className={styles.advIcon} src={product.advIcon} alt="Adv icon" />
              )}
            </div>
            <div className={styles['price-container']}>
              <div>
                {!!oldPrice ? (
                  <div className={styles.price}>
                    <p className={styles['price__old']}>
                      {isWeightGoods
                        ? `${pricePerKg} ${currency}/${t('product:kg')}`
                        : `${oldPrice} ${currency}`}
                    </p>
                    <p className={styles['price__current']}>
                      {isWeightGoods
                        ? `${currentPriceWeightGood} ${currency}/кг`
                        : `${currentPrice} ${currency}`}
                    </p>
                    {(Number(discount) > 0 || fixedDiscountValue) && (
                      <span className={styles['price__discount']}>
                        {Number(discount) > 0
                          ? `-${discount}%`
                          : `-${fixedDiscountValue} ${currency}`}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className={styles.price}>
                    <p className={styles['price__current']}>
                      {isWeightGoods
                        ? `${currentPriceWeightGood} ${currency}/кг`
                        : `${currentPrice} ${currency}`}
                    </p>
                  </div>
                )}
                <div className={styles.title}>
                  <span className={styles.quant}>
                    {isWeightGoods ? `(${t('product:quant')} ${weight})` : weight}
                  </span>
                </div>
              </div>
              <div className={styles.buttons}>
                {qty <= 0 && (
                  <WaitButton
                    isWait={isWait}
                    productId={product.id}
                    queryKey={`products/${productSlug}`}
                  />
                )}
                <FavoriteButton
                  isFavorite={isFavorite}
                  productId={product.id}
                  queryKey={`products/${productSlug}`}
                />
                {inCart ? (
                  <UpDownButton
                    value={inCart.qty}
                    maxValue={inCart.maxQty}
                    onClick={newQty =>
                      setProductQty({ productId: Number(product.id), qty: newQty })
                    }
                  />
                ) : (
                  <Button
                    data-gtm-id="AddToCartButton"
                    size="small"
                    disabled={qty <= 0}
                    onClick={() => addProduct(product)}
                  >
                    {qty <= 0 ? t('product:zeroQty') : t('product:addToCart')}
                  </Button>
                )}
              </div>
            </div>
            <p className={styles.description}>{description}</p>
            {(calorie || protein || fat || carbohydrate) && (
              <>
                <p className={styles['nutrients-header']}>{t('product:100g')}:</p>
                <div className={styles.nutrients}>
                  {calorie && (
                    <span className={styles.nutrient}>
                      <p>{t('product:calories')}</p>
                      <p>{calorie}</p>
                    </span>
                  )}
                  {protein && (
                    <span className={styles.nutrient}>
                      <p>{t('product:proteins')}</p>
                      <p>
                        {protein} {t('product:g')}
                      </p>
                    </span>
                  )}
                  {fat && (
                    <span className={styles.nutrient}>
                      <p>{t('product:fats')}</p>
                      <p>
                        {fat} {t('product:g')}
                      </p>
                    </span>
                  )}
                  {carbohydrate && (
                    <span className={styles.nutrient}>
                      <p>{t('product:carbohydrates')}</p>
                      <p>
                        {carbohydrate} {t('product:g')}
                      </p>
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className={styles.specifications}>
          <Accordion>
            <AccordionSummary>
              {({ isExpanded, toggle }) => (
                <div className={styles.specification}>
                  <b>{t('product:manufacturer')}</b>
                  <p>{manufacturer}</p>
                  <Button variant="text" onClick={toggle}>
                    {isExpanded ? t('product:lessSpecifications') : t('product:moreSpecifications')}
                  </Button>
                </div>
              )}
            </AccordionSummary>
            <AccordionDetails>
              {importer && (
                <div className={styles.specification}>
                  <b>{t('product:importer')}</b>
                  <p>{importer}</p>
                </div>
              )}
              {composition && (
                <div className={styles.specification}>
                  <b>{t('product:composition')}</b>
                  <p>{composition}</p>
                </div>
              )}
              {expiryFull && (
                <div className={styles.specification}>
                  <b>{t('product:expiry')}</b>
                  <p>{expiryFull}</p>
                </div>
              )}
              {regulatoryDocument && (
                <div className={styles.specification}>
                  <b>{t('product:regulatoryDocument')}</b>
                  <p>{regulatoryDocument}</p>
                </div>
              )}
              {!!pricePerKg && (
                <div className={styles.specification}>
                  <b>{t('product:pricePerKg')}</b>
                  <p>
                    {pricePerKg}
                    {currency}
                  </p>
                </div>
              )}
            </AccordionDetails>
          </Accordion>
        </div>
        <div className={styles.reviews}>
          <div className={styles['reviews__side']}>
            <ReviewsTotal rating={rating} reviews={reviews} />
            {!isReviewed && customer && (
              <Button
                onClick={() => {
                  const { modalId } = openModal(
                    <ProductReviewModal
                      productId={product.id}
                      productSlug={product.slug}
                      productImg={product.img}
                      onClose={() => closeModal(modalId)}
                    />,
                  );
                }}
              >
                {t('product:addProductReview')}
              </Button>
            )}
          </div>
          <Reviews reviews={reviews} />
        </div>
      </div>
    </>
  );
};

export default Product;
